// Karttaruutujen paikallinen välimuisti (offline-kartta).
//
// Ruudut tallennetaan selaimen Cache API:in, joka toimii myös Capacitorin
// WebView'ssä (osoite https://localhost). Välimuistiavain EI sisällä
// API-avainta: se on vain kuvitteellinen osoite "kerros/z/x/y".
//
// Tiedosto on tahallaan jaettu kolmeen osaan, jotta jokainen on testattavissa
// ilman selainta:
//   1. ruutulaskenta   – mitkä ruudut kattavat alueen (planPrefetch)
//   2. välimuisti      – createTileCache (Cache API injektoidaan testeissä)
//   3. haku ja lataus  – loadTile (näyttö) ja prefetchTiles (esilataus)

import { tileKey } from "./geo.js";

/** Pohjakartan tarkin natiivi zoom (sama kuin basemap.js:n maxNativeZoom). */
export const MAX_NATIVE_ZOOM = 16;
/** Ladattua ruutua pidetään tuoreena näin kauan; sen jälkeen haetaan uusi, jos verkko on. */
export const TILE_TTL_MS = 30 * 24 * 3600 * 1000;
/** Keskimääräinen MML-ruudun koko arviota varten (PNG, 256 px). */
export const APPROX_TILE_BYTES = 22_000;

const CACHE_NAME = "tilehunting-tiles-v1";
const KEY_ORIGIN = "https://tiles.invalid"; // vain avain, osoitetta ei koskaan haeta

// ---------------------------------------------------------------------------
// 1. Ruutulaskenta
// ---------------------------------------------------------------------------

/** Alueen kattavat ruutuindeksit zoomilla z. Reunat rajataan maailman sisään. */
export function tileRange({ south, west, north, east }, z) {
  const last = 2 ** z - 1;
  // Web Mercator ulottuu vain ±85,05° asti; sen yli tileKey antaisi NaN.
  const lat = (v) => Math.max(-85.0511, Math.min(85.0511, v));
  const lon = (v) => Math.max(-180, Math.min(179.9999999, v));
  const [, x0, y1] = tileKey(lat(south), lon(west), z).split("/").map(Number); // lounainen kulma: pieni x, suuri y
  const [, x1, y0] = tileKey(lat(north), lon(east), z).split("/").map(Number); // koillinen kulma: suuri x, pieni y
  const clamp = (v) => Math.max(0, Math.min(last, v));
  return { x0: clamp(Math.min(x0, x1)), x1: clamp(Math.max(x0, x1)), y0: clamp(Math.min(y0, y1)), y1: clamp(Math.max(y0, y1)) };
}

/** Ruutujen määrä zoomilla z. */
export function countAtZoom(bounds, z) {
  const r = tileRange(bounds, z);
  return (r.x1 - r.x0 + 1) * (r.y1 - r.y0 + 1);
}

/**
 * Suunnittelee esilatauksen. Ladataan zoomit minZoom..top. Jos ruutuja tulisi
 * yli maxTiles, tarkin zoom pudotetaan, kunnes määrä mahtuu (capped = true).
 * Jos jo pelkkä minZoom ylittää rajan, alue on liian suuri (tooLarge = true).
 */
export function planPrefetch(bounds, { minZoom, maxZoom = MAX_NATIVE_ZOOM, maxTiles = 4000 }) {
  const low = Math.max(0, Math.min(minZoom, maxZoom));
  const total = (top) => {
    let sum = 0;
    for (let z = low; z <= top; z++) sum += countAtZoom(bounds, z);
    return sum;
  };
  let top = maxZoom;
  while (top > low && total(top) > maxTiles) top--;
  const count = total(top);
  const zooms = [];
  for (let z = low; z <= top; z++) zooms.push(z);
  return {
    bounds,
    zooms,
    count,
    capped: top < maxZoom,
    tooLarge: count > maxTiles,
    bytes: count * APPROX_TILE_BYTES,
  };
}

/** Käy suunnitelman ruudut läpi karkeasta tarkkaan: [z, x, y]. */
export function* iterateTiles(plan) {
  for (const z of plan.zooms) {
    const r = tileRange(plan.bounds, z);
    for (let y = r.y0; y <= r.y1; y++) {
      for (let x = r.x0; x <= r.x1; x++) yield [z, x, y];
    }
  }
}

// ---------------------------------------------------------------------------
// 2. Välimuisti
// ---------------------------------------------------------------------------

const keyUrl = (layer, z, x, y) => `${KEY_ORIGIN}/${layer}/${z}/${x}/${y}`;

/**
 * Cache API:n päälle tehty ruutuvarasto. Kaikki virheet (puuttuva Cache API,
 * täysi levy, ei-turvallinen konteksti) nielaistaan: kartta toimii silloin
 * ilman välimuistia, kuten ennen tätä ominaisuutta.
 */
export function createTileCache({ cacheStorage = globalThis.caches, now = Date.now } = {}) {
  let opening = null;

  function open() {
    if (!cacheStorage) return Promise.resolve(null);
    opening ??= cacheStorage.open(CACHE_NAME).catch(() => {
      opening = null;
      return null;
    });
    return opening;
  }

  return {
    available: () => Boolean(cacheStorage),

    /** Tallennettu ruutu ja sen tallennusaika, tai null. */
    async read(layer, z, x, y) {
      try {
        const cache = await open();
        const response = await cache?.match(keyUrl(layer, z, x, y));
        if (!response) return null;
        return { blob: await response.blob(), cachedAt: Number(response.headers.get("x-cached-at")) || 0 };
      } catch {
        return null;
      }
    },

    /** Vain tallennusaika (ei lue kuvaa): esilataus ohittaa näin jo tuoreet ruudut. */
    async cachedAt(layer, z, x, y) {
      try {
        const cache = await open();
        const response = await cache?.match(keyUrl(layer, z, x, y));
        return response ? Number(response.headers.get("x-cached-at")) || 0 : null;
      } catch {
        return null;
      }
    },

    /** Tallentaa ruudun. Palauttaa false, jos tallennus ei onnistunut (esim. levy täynnä). */
    async write(layer, z, x, y, blob) {
      try {
        const cache = await open();
        if (!cache) return false;
        await cache.put(
          keyUrl(layer, z, x, y),
          new Response(blob, { headers: { "content-type": blob.type || "image/png", "x-cached-at": String(now()) } }),
        );
        return true;
      } catch {
        return false;
      }
    },

    /** Tallennettujen ruutujen määrä (kaikki kerrokset). */
    async count() {
      try {
        const cache = await open();
        return cache ? (await cache.keys()).length : 0;
      } catch {
        return 0;
      }
    },

    /** Poistaa kaikki ladatut ruudut. */
    async clear() {
      try {
        opening = null;
        return cacheStorage ? await cacheStorage.delete(CACHE_NAME) : false;
      } catch {
        return false;
      }
    },
  };
}

/** Sovelluksen yhteinen välimuisti. */
export const tileCache = createTileCache();

// ---------------------------------------------------------------------------
// 3. Haku ja lataus
// ---------------------------------------------------------------------------

/**
 * Yhden ruudun haku näyttöä varten: tuore välimuistiruutu ensin, muuten verkosta
 * (ja talteen). Jos verkko ei vastaa, vanhentunutkin ruutu on parempi kuin tyhjä.
 * Palauttaa { blob, source }, source on "cache", "network" tai "stale".
 */
export async function loadTile({ cache, layer, z, x, y, fetchTile, ttlMs = TILE_TTL_MS, now = Date.now }) {
  const hit = await cache.read(layer, z, x, y);
  if (hit && now() - hit.cachedAt < ttlMs) return { blob: hit.blob, source: "cache" };
  try {
    const blob = await fetchTile(z, x, y);
    await cache.write(layer, z, x, y, blob);
    return { blob, source: "network" };
  } catch (error) {
    if (hit) return { blob: hit.blob, source: "stale" };
    throw error;
  }
}

/**
 * Lataa ruudut välimuistiin rinnakkain muutamalla yhteydellä.
 *
 * tiles:      iteroitava [z, x, y] (ks. iterateTiles)
 * total:      ruutujen määrä edistymispalkkia varten
 * fetchTile:  (z, x, y, signal) => Promise<Blob>
 * signal:     AbortSignal; keskeytys jättää jo ladatut ruudut talteen
 *
 * Jos useampi peräkkäinen haku epäonnistuu (verkko poikki, palvelimella ei MML-avainta),
 * lataus lopetetaan kesken: yksittäinen puuttuva ruutu ei sitä vielä pysäytä.
 */
export async function prefetchTiles({
  tiles, total, layer, cache, fetchTile,
  concurrency = 4, signal, onProgress,
  maxConsecutiveFailures = 10, ttlMs = TILE_TTL_MS, now = Date.now,
}) {
  const result = { total, fetched: 0, skipped: 0, failed: 0, aborted: false, stoppedByNetwork: false };
  const iterator = tiles[Symbol.iterator]();
  let consecutiveFailures = 0;
  const report = () => onProgress?.({ ...result, done: result.fetched + result.skipped + result.failed });

  async function worker() {
    for (;;) {
      if (signal?.aborted) { result.aborted = true; return; }
      if (result.stoppedByNetwork) return;
      const next = iterator.next();
      if (next.done) return;
      const [z, x, y] = next.value;
      const stamp = await cache.cachedAt(layer, z, x, y);
      if (stamp !== null && now() - stamp < ttlMs) {
        result.skipped++;
        report();
        continue;
      }
      try {
        const blob = await fetchTile(z, x, y, signal);
        const stored = await cache.write(layer, z, x, y, blob);
        if (!stored) throw new Error("Ruudun tallennus puhelimeen epäonnistui");
        result.fetched++;
        consecutiveFailures = 0;
      } catch {
        if (signal?.aborted) { result.aborted = true; return; }
        result.failed++;
        if (++consecutiveFailures >= maxConsecutiveFailures) result.stoppedByNetwork = true;
      }
      report();
    }
  }

  await Promise.all(Array.from({ length: Math.max(1, concurrency) }, worker));
  return result;
}
